/**
 * Internationalization system for SpeedCurve Metronome (v1.6 - Final UX Refinements)
 *
 * =====================================================================================
 * JOURNAL DES MODIFICATIONS (FINAL UX REFINEMENTS)
 *
 * AMÉLIORATION UX :
 * 1.  HELIOS: Ajout de la méthode `getOtherLanguage()` pour encapsuler la logique de bascule.
 *     Le module est désormais seul responsable de savoir quelle est l'autre langue disponible,
 *     améliorant la séparation des responsabilités.
 *
 * 2.  NOUVELLES TRADUCTIONS POUR LA THÉORIE RYTHMIQUE :
 *     - Ajout de clés pour les titres et descriptions des sections de théorie rythmique
 *       dans la modale d'information, expliquant les groupements binaires, ternaires et asymétriques.
 *
 * 3.  NOUVELLES TRADUCTIONS POUR LE GROUPEMENT RYTHMIQUE PERSONNALISÉ :
 *     - Ajout de clés pour le label du champ de saisie, les messages d'information
 *       (valide, erreur de format, erreur de somme) et le label d'affichage du pattern
 *       dans les informations de signature.
 *
 * 4.  NOUVEAU RESSENTI POUR LES GROUPEMENTS PERSONNALISÉS :
 *     - Ajout d'une traduction pour le "feeling" `custom_grouping`.
 * =====================================================================================
 */
export class I18n {
    constructor() {
        this.currentLanguage = 'en';
        this.translations = {};
        this.loadTranslations();
    }

    async initialize() {
        const savedLang = localStorage.getItem('language') || 'en';
        this.setLanguage(savedLang);
    }

    loadTranslations() {
        this.translations = {
            en: {
                // --- General Interface ---
                app_name: 'SpeedCurve Metronome',
                loading_audio: 'Initializing audio engine...',
                training_program: 'Training Program',
                target_tempo: 'Target Tempo (BPM)',
                start_tempo: 'Start Tempo (BPM)',
                sprint_tempo: 'Sprint Tempo (BPM)',
                comfort_zone_tempo: 'Comfort Zone (BPM)',
                increment: 'Increment (BPM)',
                custom_tempo_points: 'Custom Tempo Points',
                add_point: 'Add Point',
                beats_per_measure: 'Beats per Measure',
                time_unit: 'Time Unit',
                half_note: 'Half Note (1/2)',
                quarter_note: 'Quarter Note (1/4)',
                eighth_note: 'Eighth Note (1/8)',
                sixteenth_note: 'Sixteenth Note (1/16)',
                thirtysecond_note: 'Thirty-second Note (1/32)',
                measures_per_step: 'Measures per Step',
                sound_type: 'Sound Type',
                show_advanced_settings: 'Advanced Settings',
                hide_advanced_settings: 'Hide Advanced Settings',

                // --- Subdivision Controls ---
                subdivision_mode: 'Subdivision Mode',
                subdivision_none: 'None',
                subdivision_binary: 'Binary (Eighths)',
                subdivision_ternary: 'Ternary (Triplets)',
                subdivision_swing: 'Swing',

                // --- Advanced Rhythmic Controls ---
                accent_pattern: 'Accent Pattern',
                reset_accents: 'Reset Accents',
                accent_strength: 'Accent Strength',
                feeling: 'Feeling',
                strong_beats: 'Strong Beats',
                group: 'Group',
                grouping_pattern: 'Grouping', // NEW: Grouping pattern label

                // NEW: Custom Grouping
                custom_grouping: 'Custom Grouping',
                custom_grouping_placeholder: 'e.g., 2,2,3 for 7/4',
                custom_grouping_info: 'Define beat grouping (e.g., 2,2,3). Sum must match beats per measure.',
                custom_grouping_info_valid: 'Custom grouping applied. Sum matches beats per measure.',
                custom_grouping_error_sum: 'Error: Sum of groups ({sum}) does not match beats per measure ({beatsPerMeasure}).',
                custom_grouping_error_format: 'Error: Invalid format. Use comma-separated numbers (e.g., 2,2,3).',

                // --- Time Signature Types ---
                simple_time: 'Simple Time',
                compound_time: 'Compound Time',
                asymmetric_time: 'Asymmetric Time',
                irregular_time: 'Irregular Time',

                // --- Time Signature Feelings ---
                march_feeling: 'March',
                waltz_feeling: 'Waltz',
                common_feeling: 'Common Time',
                lilting_feeling: 'Lilting',
                compound_triple_feeling: 'Compound Triple',
                compound_quadruple_feeling: 'Compound Quadruple',
                asymmetric_feeling: 'Asymmetric',
                irregular_feeling: 'Irregular',
                complex_feeling: 'Complex',
                simple_generated_feeling: 'Simple (Custom)',
                compound_generated_feeling: 'Compound (Custom)',
                custom_grouping_feeling: 'Custom Grouping', // NEW: Feeling for user-defined groupings


                // --- Sound Types & Options ---
                classic: 'Classic Tick',
                snap: 'Finger Snap',
                perc: 'Percussive Hit',
                wood: 'Wood Block',
                volume: 'Volume',
                visual_flash: 'Visual Flash',
                swing_ratio: 'Swing Ratio',
                swing_info: '55% = Light • 60% = Moderate • 66% = Classic • 75% = Heavy',
                select_program: 'Select a training program to see its description.',

                // --- Programs ---
                program_pyramid: 'Progressive Pyramid',
                program_consolidation: 'Consolidation',
                program_endurance: 'Endurance',
                program_speed: 'Maximum Speed',
                program_agility: 'Agility',
                program_ladder: 'Graduated Ladder',
                program_custom: 'Custom',

                // --- Program descriptions ---
                program_pyramid_desc:
                    "<strong>Unleash Your Potential.</strong><br>The Progressive Pyramid builds your speed to a peak beyond your target, then solidifies your technique on the way down.<br>The ultimate workout for breakthrough performance and flawless adaptation.",
                program_consolidation_desc:
                    "<strong>Achieve Effortless Mastery.</strong><br>Alternate between your 'Comfort Zone' tempo and 'Sprint' tempo.<br>This drill is perfect for making complex passages feel automatic and fluid.",
                program_endurance_desc:
                    "<strong>Build Unshakeable Stamina.</strong><br>The Endurance workout gets you to your target tempo and keeps you there.<br>Forge rock-solid muscle memory and the consistency to perform longer, stronger.",
                program_speed_desc:
                    "<strong>Shatter Your Speed Limits.</strong><br>The Maximum Speed program is a shock to the system, pushing you far beyond your comfort zone to an audacious new peak.<br>Designed to demolish speed plateaus and unlock new levels of velocity.",
                program_agility_desc:
                    "<strong>Master Rhythmic Dexterity.</strong><br>The Agility workout forces rapid shifts between a comfortable cruise and your target speed.<br>The key to sharpening your rhythmic control and tempo-shifting reflexes.",
                program_ladder_desc:
                    "<strong>The Path to Precision.</strong><br>The Graduated Ladder offers a perfectly controlled, step-by-step ascent in tempo.<br>Methodical, precise, and fully customizable, it's the ideal program for building a flawless foundation.",
                program_custom_desc:
                    "<strong>Your Workout, Your Rules.</strong><br>The Custom program gives you the drawing board.<br>Design the exact tempo curve your practice demands, with absolute control over every point.<br>Your personal trainer, built by you.",

                // --- Transport controls ---
                start: 'Start',
                stop: 'Stop',
                pause: 'Pause',
                resume: 'Resume',
                reset_position: 'Reset Step',

                // --- Status & Dialogs ---
                ready: 'Ready',
                paused: 'Paused',
                step: 'Step',
                steps: 'Steps',
                tempo: 'Tempo',
                tempo_unit_bpm: 'BPM',
                error_title: 'Error',
                start_impossible: 'Could not start playback',
                program_complete_title: 'Program Complete!',
                program_complete_message:
                    'Congratulations! You have completed the training program.',
                sound_load_error_title: 'Audio Warning',
                sound_load_error_message:
                    'Some sounds could not be loaded and will be unavailable:',
                click_to_modify: 'Click to modify',
                measure: 'Measure',
                program_too_long_error:
                    'Program is too long to start. The estimated {estimatedEvents} events exceed the maximum of {maxEvents}. Please reduce settings.',

                // --- Tooltips ---
                reset_tooltip_active: 'Restart the current step from the beginning.',
                reset_tooltip_disabled: 'Reset is only available when playback is paused.',

                // --- Info Modal ---
                info_modal_title: 'About SpeedCurve Metronome',
                info_modal_controls_title: 'Header Controls Explained',
                theme_toggle_label: 'Theme',
                info_btn_label: 'Info',
                feedback_btn_label: 'Feedback',
                language_toggle_label: 'Language',
                info_modal_controls_theme_desc: 'Switches the application between a light and dark visual theme.',
                info_modal_controls_info_desc: 'Opens this information window to learn about features and programs.',
                info_modal_controls_feedback_desc: 'Allows you to send us your suggestions or report any bugs you find.',
                info_modal_controls_language_desc: 'Toggles the interface language between English (EN) and French (FR).',
                
                rhythmic_theory_title: 'Understanding Rhythmic Grouping', // NEW
                rhythmic_theory_explanation: 'Musical time signatures group beats into larger units. This application uses standard musical theory to group beats, which helps in perceiving the rhythm more intuitively. Generally, beats are grouped by 2s or 3s, forming simple, compound, or asymmetric meters.', // NEW
                simple_time_theory_label: 'Simple Time', // NEW
                simple_time_theory_desc: 'Beats are divisible by two (e.g., 2/4, 3/4, 4/4). Each main beat subdivides into two smaller units.', // NEW
                compound_time_theory_label: 'Compound Time', // NEW
                compound_time_theory_desc: 'Beats are divisible by three (e.g., 6/8, 9/8, 12/8). Each main beat subdivides into three smaller units, giving a "lilting" or "swinging" feel.', // NEW
                asymmetric_time_theory_label: 'Asymmetric/Irregular Time', // NEW
                asymmetric_time_theory_desc: 'Combinations of two- and three-beat groupings (e.g., 5/4 = 3+2, 7/8 = 2+2+3). These create unique, often driving, rhythmic feels that challenge conventional symmetry.', // NEW


                info_modal_intro:
                    "This is more than a metronome. It's a professional training tool designed to elevate your rhythmic precision, speed, and control through scientifically-backed practice programs.",
                info_modal_features_title: 'Core Features',
                info_modal_features_list: `
                    <li><strong>Advanced Training Programs:</strong> Go beyond a simple tick. Choose from scientifically-designed programs like 'Progressive Pyramid' to break speed plateaus, 'Endurance' for consistency, or 'Agility' to master tempo changes. You can also create your own custom tempo curve.</li>
                    <li><strong>Complete Rhythmic Control:</strong> Master any rhythm. Full control over time signatures (from simple 4/4 to complex 13/8), subdivisions (eighths, triplets, swing), and accent patterns. Define your own beat groupings (e.g., 2+3+2 for a 7/4) for perfect practice.</li>
                    <li><strong>High-Precision Engine:</strong> Timing is everything. The metronome runs in a separate process (Web Worker) to ensure rock-solid, ultra-precise timing that is not affected by UI updates or animations, delivering professional-grade accuracy.</li>
                    <li><strong>Interactive Visual Feedback:</strong> See your progress. The dynamic graph visualizes your entire training session, while the beat indicators and metronome circle provide real-time visual cues for every beat and subdivision, keeping you perfectly in sync.</li>
                    <li><strong>Multilingual & Themed Interface:</strong> Practice in comfort. Switch instantly between English and French, and choose between a light or dark theme to suit your environment and preference.</li>
                    <li><strong>Presets System:</strong> Save, load, and manage your favorite configurations. Perfect for switching between different practice setups instantly. Click the 📁 button, enter a name for your current configuration, and click Save. To load a preset, click Load next to its name. To delete, click Delete.</li>
                    <li><strong>Auto-Save:</strong> Your settings are automatically saved and restored when you return, so you can pick up right where you left off.</li>`,
                info_modal_programs_title: 'Training Programs Explained',
                info_pyramid_title: 'Progressive Pyramid',
                info_pyramid_desc:
                    "<strong>Goal:</strong> Break through speed plateaus.<br><strong>Science:</strong> This program uses <strong>Post-Activation Potentiation (PAP)</strong>, a method used by elite athletes. You first ascend to a tempo <em>above</em> your target (overload), which tricks your nervous system into 'recruiting' more resources. When you descend back to your target tempo, it feels significantly easier, effectively 'locking in' your new skill level.",
                info_consolidation_title: 'Consolidation',
                info_consolidation_desc:
                    "<strong>Goal:</strong> Make complex passages feel effortless and automatic.<br><strong>Science:</strong> This is a form of <strong>interleaved practice</strong>. By constantly switching between a comfortable 'work zone' (your start tempo) and your target speed, you prevent your brain from going on autopilot. This forces active recall and problem-solving on every sprint, engraving the motor sequence much more deeply than simple repetition.",
                info_endurance_title: 'Endurance',
                info_endurance_desc:
                    "<strong>Goal:</strong> Build stamina and consistency for long performances.<br><strong>Science:</strong> This program targets <strong>muscle memory and metabolic resistance</strong>. Maintaining a steady tempo for an extended period strengthens neural pathways, making the required movements more efficient and less taxing. This builds the physical and mental stamina to perform without degradation in quality or timing over time.",
                info_speed_title: 'Maximum Speed',
                info_speed_desc:
                    "<strong>Goal:</strong> Shock your system to unlock new levels of velocity.<br><strong>Science:</strong> This program is a direct application of <strong>overspeed training</strong>. After reaching your target tempo, the algorithm pushes you into uncharted territory with small, incremental bursts of speed. This forces your neuromuscular system to adapt by firing faster and more efficiently, effectively raising your absolute speed ceiling.",
                info_agility_title: 'Agility',
                info_agility_desc:
                    "<strong>Goal:</strong> Master tempo changes and improve rhythmic control.<br><strong>Science:</strong> This drill enhances your <strong>executive functions</strong>—specifically your brain's ability to task-switch and adapt. The rapid and unpredictable shifts between a cruise speed and your target speed train your internal clock to be more flexible and responsive, a critical skill for ensemble playing and expressive performance.",
                info_ladder_title: 'Graduated Ladder',
                info_ladder_desc:
                    "<strong>Goal:</strong> Methodical, controlled tempo increase or decrease.<br><strong>Science:</strong> Based on the principle of <strong>progressive overload</strong>, this is the most fundamental and controlled way to build speed. Each step provides just enough challenge to stimulate adaptation without causing tension or bad habits. It's the perfect tool for building a solid foundation or methodically warming up.",
                info_custom_title: 'Custom',
                info_custom_desc:
                    "<strong>Goal:</strong> Your workout, your rules.<br><strong>Science:</strong> This is your laboratory. Create your own programs to target specific weaknesses or mimic the exact tempo structure of a piece you're working on. The science here is <strong>specificity</strong>—designing the perfect drill for the exact problem you need to solve.",

                // --- Feedback Modal ---
                send_feedback: 'Send Feedback',
                feedback_modal_title: 'Send Feedback',
                feedback_type_label: 'Type of Feedback',
                feedback_type_suggestion: 'Suggestion',
                feedback_type_bug: 'Bug Report',
                feedback_description_label: 'Description',
                feedback_description_placeholder: 'Please describe your suggestion or the bug in detail. For bugs, include steps to reproduce.',
                feedback_submit: 'Send',
                feedback_sending: 'Sending...',
                feedback_success_title: 'Feedback Sent!',
                feedback_success_message: 'Thank you for your feedback! We have received your submission and will review it shortly.',
                feedback_error_generic: 'An error occurred while sending your feedback. Please try again later.',
                feedback_validation_type: 'Please select a feedback type.',
                feedback_validation_description: 'Description must be at least 10 characters long.',

                // --- Presets ---
                presets_title: 'Presets',
                presets_btn_label: 'Presets',
                save_preset: 'Save Current',
                preset_name_placeholder: 'Preset name',
                saved_presets: 'Saved Presets',
                no_presets: 'No saved presets',
                load: 'Load',
                delete: 'Delete',
                preset_loaded: 'Preset Loaded',
                preset_name_required: 'Please enter a preset name',
                confirm_delete_preset: 'Are you sure you want to delete this preset?',

                // --- Changelog ---
                changelog_title: 'Changelog',
                changelog_btn_label: 'Changelog',
                changelog_v27_title: 'v2.7',
                changelog_v27_desc1: 'Progression System: Track sessions, stats, achievements, XP/levels',
                changelog_v27_desc2: 'Practice Heatmap: GitHub-style 12-week activity grid',
                changelog_v27_desc3: 'Export/Import: Backup all data as JSON file',
                changelog_v26_title: 'v2.6',
                changelog_v26_desc1: 'Presets: Save/load/delete named configurations',
                changelog_v26_desc2: 'Auto-Save: Settings persist across sessions',
                changelog_v26_desc3: 'Changelog: New button to track updates',
                changelog_v25_title: 'v2.5',
                changelog_v25_desc1: 'Bundle Fix: Resolved Vite duplication issue',
                changelog_v25_desc2: 'UI Restoration: Beat indicators fixed',

                // --- Stats & Progress Dashboard ---
                stats_title: 'Statistics',
                stats_btn_label: 'Stats',
                stats_dashboard: 'Dashboard',
                stats_total_sessions: 'Total Sessions',
                stats_total_time: 'Total Practice Time',
                stats_average_session: 'Avg Session',
                stats_current_streak: 'Current Streak',
                stats_longest_streak: 'Longest Streak',
                stats_days: 'days',
                stats_minutes: 'min',
                stats_this_week: 'This Week',
                stats_this_month: 'This Month',
                stats_all_time: 'All Time',
                stats_max_tempo: 'Max Tempo',
                stats_level: 'Level',
                stats_xp: 'XP',
                stats_no_data: 'No practice data yet',
                stats_start_practicing: 'Start practicing to see your stats!',
                heatmap_day_labels: 'S M T W T F S',

                // --- Achievements ---
                achievements_title: 'Achievements',
                achievement_first_session: 'First Steps',
                achievement_first_session_desc: 'Complete your first session',
                achievement_century: 'Century',
                achievement_century_desc: 'Complete 100 sessions',
                achievement_pyramid_10: 'Pyramid Practitioner',
                achievement_pyramid_10_desc: 'Complete 10 Pyramid sessions',
                achievement_endurance_10: 'Endurance Master',
                achievement_endurance_10_desc: 'Complete 10 Endurance sessions',
                achievement_ladder_20: 'Ladder Climber',
                achievement_ladder_20_desc: 'Complete 20 Ladder sessions',
                achievement_speed_200: 'Speed Demon',
                achievement_speed_200_desc: 'Reach 200 BPM',
                achievement_marathon: 'Marathon',
                achievement_marathon_desc: 'Complete a 50+ minute session',
                achievement_consistency_7: 'Week Warrior',
                achievement_consistency_7_desc: 'Maintain a 7-day streak',
                achievement_comeback: 'Comeback',
                achievement_comeback_desc: 'Return to practice after a break',
                achievement_explorer: 'Explorer',
                achievement_explorer_desc: 'Try all 7 training programs',
                achievement_unlocked: 'Achievement Unlocked!',

                // --- Export/Import ---
                export_title: 'Export Data',
                import_title: 'Import Data',
                export_btn_label: 'Export',
                import_btn_label: 'Import',
                export_all: 'Export All Data',
                export_success: 'Data exported successfully',
                export_error: 'Export failed',
                import_data: 'Import Data',
                import_success: 'Data imported successfully',
                import_error: 'Import failed: Invalid file',
                import_confirm: 'Replace existing data?',
                import_merge: 'Merge',
                import_replace: 'Replace',
            },

            fr: {
                // --- Interface Générale ---
                app_name: 'SpeedCurve Metronome',
                loading_audio: 'Initialisation du moteur audio...',
                training_program: "Programme d'entraînement",
                target_tempo: 'Tempo cible (BPM)',
                start_tempo: 'Tempo départ (BPM)',
                sprint_tempo: 'Tempo de Sprint (BPM)',
                comfort_zone_tempo: 'Zone de Confort (BPM)',
                increment: 'Incrément (BPM)',
                custom_tempo_points: 'Points de tempo personnalisés',
                add_point: 'Ajouter un point',
                beats_per_measure: 'Temps par mesure',
                time_unit: 'Unité de temps',
                half_note: 'Blanche (1/2)',
                quarter_note: 'Noire (1/4)',
                eighth_note: 'Croche (1/8)',
                sixteenth_note: 'Double-croche (1/16)',
                thirtysecond_note: 'Triple-croche (1/32)',
                measures_per_step: 'Mesures par palier',
                sound_type: 'Type de son',
                show_advanced_settings: 'Réglages Avancés',
                hide_advanced_settings: 'Masquer Réglages',

                // --- Contrôles de subdivision ---
                subdivision_mode: 'Mode de subdivision',
                subdivision_none: 'Aucune',
                subdivision_binary: 'Binaire (croches)',
                subdivision_ternary: 'Ternaire (triolets)',
                subdivision_swing: 'Swing',

                // --- Contrôles Rythmiques Avancés ---
                accent_pattern: "Pattern d'accents",
                reset_accents: 'Réinitialiser les accents',
                accent_strength: 'Intensité des accents',
                feeling: 'Ressenti',
                strong_beats: 'Temps forts',
                group: 'Groupe',
                grouping_pattern: 'Groupement', // NEW: Grouping pattern label

                // NEW: Custom Grouping
                custom_grouping: 'Groupement personnalisé',
                custom_grouping_placeholder: 'ex: 2,2,3 pour 7/4',
                custom_grouping_info: 'Définissez le groupement des temps (ex: 2,2,3). La somme doit correspondre aux temps par mesure.',
                custom_grouping_info_valid: 'Groupement personnalisé appliqué. La somme correspond aux temps par mesure.',
                custom_grouping_error_sum: 'Erreur : La somme des groupes ({sum}) ne correspond pas aux temps par mesure ({beatsPerMeasure}).',
                custom_grouping_error_format: 'Erreur : Format invalide. Utilisez des nombres séparés par des virgules (ex: 2,2,3).',

                // --- Types de Signatures Temporelles ---
                simple_time: 'Temps simple',
                compound_time: 'Temps composé',
                asymmetric_time: 'Temps asymétrique',
                irregular_time: 'Temps irrégulier',

                // --- Ressenti des Signatures ---
                march_feeling: 'Marche',
                waltz_feeling: 'Valse',
                common_feeling: 'Temps commun',
                lilting_feeling: 'Chaloupé',
                compound_triple_feeling: 'Triple composé',
                compound_quadruple_feeling: 'Quadruple composé',
                asymmetric_feeling: 'Asymétrique',
                irregular_feeling: 'Irrégulier',
                complex_feeling: 'Complexe',
                simple_generated_feeling: 'Simple (perso.)',
                compound_generated_feeling: 'Composé (perso.)',
                custom_grouping_feeling: 'Groupement personnalisé', // NEW: Feeling for user-defined groupings


                // --- Types de sons et options ---
                classic: 'Tick classique',
                snap: 'Claquement de doigts',
                perc: 'Coup percussif',
                wood: 'Bloc de bois',
                volume: 'Volume',
                visual_flash: 'Flash visuel',
                swing_ratio: 'Ratio de swing',
                swing_info: '55% = Léger • 60% = Modéré • 66% = Classique • 75% = Prononcé',
                select_program: 'Sélectionnez un programme pour voir sa description.',

                // --- Programmes ---
                program_pyramid: 'Pyramide progressive',
                program_consolidation: 'Consolidation',
                program_endurance: 'Endurance',
                program_speed: 'Vitesse maximale',
                program_agility: 'Agilité',
                program_ladder: 'Échelle graduée',
                program_custom: 'Personnalisé',

                // --- Descriptions de programmes ---
                program_pyramid_desc:
                    "<strong>Libérez Votre Potentiel.</strong><br>La Pyramide Progressive propulse votre vitesse vers un pic au-delà de votre cible, puis solidifie votre technique lors de la redescente.<br>L'entraînement ultime pour des performances de rupture et une adaptation sans faille.",
                program_consolidation_desc:
                    "<strong>Atteignez la Maîtrise sans Effort.</strong><br>Alternez entre votre tempo de 'Zone de Confort' et votre tempo de 'Sprint'.<br>Cet exercice est parfait pour rendre les passages complexes automatiques et fluides.",
                program_endurance_desc:
                    "<strong>Développez une Endurance Inébranlable.</strong><br>L'entraînement Endurance vous amène à votre tempo cible et vous y maintient.<br>Forgez une mémoire musculaire à toute épreuve et la régularité pour jouer plus longtemps, plus fort.",
                program_speed_desc:
                    "<strong>Pulvérisez Vos Limites de Vitesse.</strong><br>Le programme Vitesse Maximale est un choc pour le système, vous poussant bien au-delà de votre zone de confort vers un nouveau pic audacieux.<br>Conçu pour démolir les plateaux et débloquer des niveaux de vélocité insoupçonnés.",
                program_agility_desc:
                    "<strong>Maîtrisez la Dextérité Rythmique.</strong><br>L'entraînement Agilité impose des changements rapides entre une vitesse de croisière confortable et votre objectif.<br>La clé pour affûter votre contrôle rythmique et vos réflexes de changement de tempo.",
                program_ladder_desc:
                    "<strong>La Voie de la Précision.</strong><br>L'Échelle Graduée offre une ascension en tempo parfaitement contrôlée, palier par palier.<br>Méthodique, précis et entièrement personnalisable, c'est le programme idéal pour bâtir des fondations impeccables.",
                program_custom_desc:
                    "<strong>Votre Entraînement, Vos Règles.</strong><br>Le programme Personnalisé vous donne les clés.<br>Concevez la courbe de tempo exacte que votre pratique exige, avec un contrôle absolu sur chaque point.<br>Votre coach personnel, créé par vous.",

                // --- Commandes de transport ---
                start: 'Démarrer',
                stop: 'Arrêter',
                pause: 'Pause',
                resume: 'Reprendre',
                reset_position: 'Réinit. Palier',

                // --- Statut & Dialogues ---
                ready: 'Prêt',
                paused: 'En pause',
                step: 'Palier',
                steps: 'Paliers',
                tempo: 'Tempo',
                tempo_unit_bpm: 'BPM',
                error_title: 'Erreur',
                start_impossible: 'Le démarrage de la lecture a échoué',
                program_complete_title: 'Programme Terminé !',
                program_complete_message:
                    "Félicitations ! Vous avez terminé le programme d'entraînement.",
                sound_load_error_title: 'Avertissement Audio',
                sound_load_error_message:
                    "Certains sons n'ont pas pu être chargés et seront indisponibles :",
                click_to_modify: 'Cliquez pour modifier',
                measure: 'Mesure',
                program_too_long_error:
                    "Le programme est trop long pour démarrer. Le nombre d'événements estimé ({estimatedEvents}) dépasse le maximum de {maxEvents}. Veuillez réduire les paramètres.",

                // --- Info-bulles ---
                reset_tooltip_active: 'Redémarrer le palier actuel depuis le début.',
                reset_tooltip_disabled:
                    "La réinitialisation n'est possible que lorsque la lecture est en pause.",

                // --- Modal d'Information ---
                info_modal_title: 'À propos de SpeedCurve Metronome',
                info_modal_controls_title: 'Explication des contrôles',
                theme_toggle_label: 'Thème',
                info_btn_label: 'Infos',
                feedback_btn_label: 'Feedback',
                language_toggle_label: 'Langue',
                info_modal_controls_theme_desc: "Bascule l'application entre un thème visuel clair et sombre.",
                info_modal_controls_info_desc: "Ouvre cette fenêtre d'information pour en savoir plus sur les fonctionnalités.",
                info_modal_controls_feedback_desc: 'Vous permet de nous envoyer vos suggestions ou de signaler des bugs.',
                info_modal_controls_language_desc: "Change la langue de l'interface entre le Français (FR) et l'Anglais (EN).",

                rhythmic_theory_title: 'Comprendre le Groupement Rythmique', // NEW
                rhythmic_theory_explanation: 'Les signatures rythmiques musicales regroupent les temps en unités plus grandes. Cette application utilise la théorie musicale standard pour grouper les temps, ce qui aide à percevoir le rythme plus intuitivement. Généralement, les temps sont regroupés par 2 ou par 3, formant des mesures simples, composées ou asymétriques.', // NEW
                simple_time_theory_label: 'Temps simples', // NEW
                simple_time_theory_desc: 'Les temps sont divisibles par deux (ex: 2/4, 3/4, 4/4). Chaque temps principal se subdivise en deux unités plus petites.', // NEW
                compound_time_theory_label: 'Temps composés', // NEW
                compound_time_theory_desc: 'Les temps sont divisibles par trois (ex: 6/8, 9/8, 12/8). Chaque temps principal se subdivise en trois unités plus petites, donnant un ressenti "chaloupé" ou "swing".', // NEW
                asymmetric_time_theory_label: 'Temps asymétriques/irréguliers', // NEW
                asymmetric_time_theory_desc: 'Combinaisons de groupements de deux et trois temps (ex: 5/4 = 3+2, 7/8 = 2+2+3). Ceux-ci créent des sensations rythmiques uniques, souvent entraînantes, qui défient la symétrie conventionnelle.', // NEW


                info_modal_intro:
                    "Ceci est plus qu'un métronome. C'est un outil d'entraînement professionnel conçu pour élever votre précision rythmique, votre vitesse et votre contrôle grâce à des programmes de pratique basés sur la science.",
                info_modal_features_title: 'Fonctionnalités Clés',
                info_modal_features_list: `
                    <li><strong>Programmes d'Entraînement Avancés :</strong> Allez au-delà du simple 'tic'. Choisissez parmi des programmes conçus scientifiquement comme la 'Pyramide Progressive' pour briser les plateaux de vitesse, 'Endurance' pour la régularité, ou 'Agilité' pour maîtriser les changements de tempo. Vous pouvez aussi créer votre propre courbe de tempo personnalisée.</li>
                    <li><strong>Contrôle Rythmique Complet :</strong> Maîtrisez n'importe quel rythme. Contrôle total sur les signatures rythmiques (du simple 4/4 au complexe 13/8), les subdivisions (croches, triolets, swing) et les patterns d'accentuation. Définissez vos propres groupements de temps (ex: 2+3+2 pour un 7/4) pour un entraînement parfait.</li>
                    <li><strong>Moteur de Haute Précision :</strong> Le timing est primordial. Le métronome s'exécute dans un processus séparé (Web Worker) pour garantir un timing ultra-précis et fiable, non affecté par les animations de l'interface, offrant une justesse de niveau professionnel.</li>
                    <li><strong>Feedback Visuel Interactif :</strong> Visualisez vos progrès. Le graphique dynamique affiche l'ensemble de votre session d'entraînement, tandis que les indicateurs de temps et le cercle du métronome fournissent des repères visuels en temps réel pour chaque temps et subdivision, vous gardant parfaitement synchronisé.</li>
                    <li><strong>Interface Multilingue & à Thèmes :</strong> Entraînez-vous confortablement. Basculez instantanément entre le Français et l'Anglais, et choisissez entre un thème clair ou sombre pour s'adapter à votre environnement et vos préférences.</li>
                    <li><strong>Système de Presets :</strong> Sauvegardez, chargez et gérez vos configurations préférées. Parfait pour basculer instantanément entre différents setups d'entraînement. Cliquez sur le bouton 📁, entrez un nom pour votre configuration actuelle, et cliquez sur Sauvegarder. Pour charger un preset, cliquez sur Charger à côté de son nom. Pour supprimer, cliquez sur Supprimer.</li>
                    <li><strong>Sauvegarde Automatique :</strong> Vos paramètres sont automatiquement sauvegardés et restaurés à votre retour, vous permettant de reprendre exactement où vous en étiez.</li>`,
                info_modal_programs_title: 'Explication des Programmes',
                info_pyramid_title: 'Pyramide Progressive',
                info_pyramid_desc:
                    "<strong>Objectif :</strong> Briser les plateaux de vitesse.<br><strong>La science :</strong> Ce programme utilise la <strong>Potentiation Post-Activation (PPA)</strong>, une méthode employée par les athlètes de haut niveau. Vous montez d'abord à un tempo <em>supérieur</em> à votre cible (surcharge), ce qui force votre système nerveux à 'recruter' plus de ressources. Lorsque vous redescendez à votre tempo cible, il paraît beaucoup plus facile, ce qui permet de 'verrouiller' votre nouveau niveau de compétence.",
                info_consolidation_title: 'Consolidation',
                info_consolidation_desc:
                    "<strong>Objectif :</strong> Rendre les passages complexes automatiques et sans effort.<br><strong>La science :</strong> Il s'agit d'une forme de <strong>pratique entrelacée</strong>. En alternant constamment entre une 'zone de travail' confortable (votre tempo de départ) et votre vitesse cible, vous empêchez votre cerveau de passer en pilote automatique. Cela force le rappel actif et la résolution de problèmes à chaque sprint, gravant la séquence motrice bien plus profondément qu'une simple répétition.",
                info_endurance_title: 'Endurance',
                info_endurance_desc:
                    "<strong>Objectif :</strong> Développer l'endurance et la régularité pour les longues performances.<br><strong>La science :</strong> Ce programme cible la <strong>mémoire musculaire et la résistance métabolique</strong>. Maintenir un tempo stable sur une période prolongée renforce les voies neurales, rendant les mouvements requis plus efficaces et moins fatigants. Cela construit l'endurance physique et mentale pour jouer sans dégradation de la qualité ou du timing dans le temps.",
                info_speed_title: 'Vitesse Maximale',
                info_speed_desc:
                    "<strong>Objectif :</strong> Choquer votre système pour débloquer de nouveaux niveaux de vélocité.<br><strong>La science :</strong> Ce programme est une application directe de l'<strong>entraînement en survitesse</strong> (overspeed training). Après avoir atteint votre tempo cible, l'algorithme vous pousse en territoire inconnu avec de petites rafales de vitesse incrémentales. Cela force votre système neuromusculaire à s'adapter en s'activant plus rapidement et plus efficacement, élevant ainsi votre plafond de vitesse absolu.",
                info_agility_title: 'Agilité',
                info_agility_desc:
                    "<strong>Objectif :</strong> Maîtriser les changements de tempo et améliorer le contrôle rythmique.<br><strong>La science :</strong> Cet exercice améliore vos <strong>fonctions exécutives</strong> — spécifiquement la capacité de votre cerveau à changer de tâche et à s'adapter. Les changements rapides et imprévisibles entre une vitesse de croisière et votre vitesse cible entraînent votre horloge interne à être plus flexible et réactive, une compétence essentielle pour le jeu en groupe et l'expression musicale.",
                info_ladder_title: 'Échelle Graduée',
                info_ladder_desc:
                    "<strong>Objectif :</strong> Augmentation ou diminution méthodique et contrôlée du tempo.<br><strong>La science :</strong> Basé sur le principe de la <strong>surcharge progressive</strong>, c'est le moyen le plus fondamental et contrôlé de développer la vitesse. Chaque palier offre juste assez de défi pour stimuler l'adaptation sans provoquer de tension ou de mauvaises habitudes. C'est l'outil parfait pour construire des bases solides ou pour s'échauffer méthodiquement.",
                info_custom_title: 'Personnalisé',
                info_custom_desc:
                    "<strong>Objectif :</strong> Votre entraînement, vos règles.<br><strong>La science :</strong> C'est votre laboratoire. Créez vos propres programmes pour cibler des faiblesses spécifiques ou pour imiter la structure de tempo exacte d'un morceau que vous travaillez. La science ici est la <strong>spécificité</strong> — concevoir l'exercice parfait pour le problème exact que vous devez résoudre.",

                // --- Modal de Feedback ---
                send_feedback: 'Envoyer un retour',
                feedback_modal_title: 'Envoyer un retour',
                feedback_type_label: 'Type de retour',
                feedback_type_suggestion: 'Suggestion',
                feedback_type_bug: 'Rapport de bug',
                feedback_description_label: 'Description',
                feedback_description_placeholder: "Veuillez décrire en détail votre suggestion ou le bug. Pour les bugs, incluez les étapes pour le reproduire.",
                feedback_submit: 'Envoyer',
                feedback_sending: 'Envoi en cours...',
                feedback_success_title: 'Retour envoyé !',
                feedback_success_message: "Merci pour votre retour ! Nous avons bien reçu votre envoi et nous l'examinerons bientôt.",
                feedback_error_generic: "Une erreur est survenue lors de l'envoi. Veuillez réessayer plus tard.",
                feedback_validation_type: 'Veuillez sélectionner un type de retour.',
                feedback_validation_description: 'La description doit contenir au moins 10 caractères.',

                // --- Presets ---
                presets_title: 'Presets',
                presets_btn_label: 'Presets',
                save_preset: 'Sauvegarder',
                preset_name_placeholder: 'Nom du preset',
                saved_presets: 'Presets sauvegardés',
                no_presets: 'Aucun preset sauvegardé',
                load: 'Charger',
                delete: 'Supprimer',
                preset_loaded: 'Preset chargé',
                preset_name_required: 'Veuillez entrer un nom de preset',
                confirm_delete_preset: 'Êtes-vous sûr de vouloir supprimer ce preset ?',

                // --- Changelog ---
                changelog_title: 'Changelog',
                changelog_btn_label: 'Changelog',
                changelog_v27_title: 'v2.7',
                changelog_v27_desc1: 'Système de progression: sessions, stats, achievements, XP/niveaux',
                changelog_v27_desc2: 'Heatmap de pratique: grille d\'activité 12 semaines',
                changelog_v27_desc3: 'Export/Import: sauvegarde toutes les données en JSON',
                changelog_v26_title: 'v2.6',
                changelog_v26_desc1: 'Presets: sauvegarder/charger/supprimer configurations',
                changelog_v26_desc2: 'Auto-Save: paramètres persistants entre sessions',
                changelog_v26_desc3: 'Changelog: nouveau bouton pour suivre les mises à jour',
                changelog_v25_title: 'v2.5',
                changelog_v25_desc1: 'Correction Bundle: problème de duplication Vite résolu',
                changelog_v25_desc2: 'Restauration UI: indicateurs de temps corrigés',

                // --- Stats & Progress Dashboard ---
                stats_title: 'Statistiques',
                stats_btn_label: 'Stats',
                stats_dashboard: 'Tableau de bord',
                stats_total_sessions: 'Sessions totales',
                stats_total_time: 'Temps total',
                stats_average_session: 'Moyenne',
                stats_current_streak: 'Série actuelle',
                stats_longest_streak: 'Plus longue série',
                stats_days: 'jours',
                stats_minutes: 'min',
                stats_this_week: 'Cette semaine',
                stats_this_month: 'Ce mois',
                stats_all_time: 'Total',
                stats_max_tempo: 'Tempo max',
                stats_level: 'Niveau',
                stats_xp: 'XP',
                stats_no_data: 'Aucune donnée',
                stats_start_practicing: 'Commencez à pratiquer pour voir vos stats!',
                heatmap_day_labels: 'D L M M J V S',

                // --- Achievements ---
                achievements_title: 'Succès',
                achievement_first_session: 'Premiers pas',
                achievement_first_session_desc: 'Terminez votre première session',
                achievement_century: 'Centurie',
                achievement_century_desc: 'Terminez 100 sessions',
                achievement_pyramid_10: 'Maître Pyramide',
                achievement_pyramid_10_desc: 'Terminez 10 sessions Pyramide',
                achievement_endurance_10: 'Maître Endurance',
                achievement_endurance_10_desc: 'Terminez 10 sessions Endurance',
                achievement_ladder_20: 'Maître Echelle',
                achievement_ladder_20_desc: 'Terminez 20 sessions Echelle',
                achievement_speed_200: 'Démon de vitesse',
                achievement_speed_200_desc: 'Atteignez 200 BPM',
                achievement_marathon: 'Marathon',
                achievement_marathon_desc: 'Session de 50+ minutes',
                achievement_consistency_7: 'Guerrier de la semaine',
                achievement_consistency_7_desc: 'Gardez une série de 7 jours',
                achievement_comeback: 'Retour',
                achievement_comeback_desc: 'Revenez après une pause',
                achievement_explorer: 'Explorateur',
                achievement_explorer_desc: 'Essayez les 7 programmes',
                achievement_unlocked: 'Succès débloqué!',

                // --- Export/Import ---
                export_title: 'Exporter les données',
                import_title: 'Importer les données',
                export_btn_label: 'Exporter',
                import_btn_label: 'Importer',
                export_all: 'Exporter tout',
                export_success: 'Données exportées avec succès',
                export_error: 'Échec de l\'export',
                import_data: 'Importer les données',
                import_success: 'Données importées avec succès',
                import_error: 'Import échoué: Fichier invalide',
                import_confirm: 'Remplacer les données existantes?',
                import_merge: 'Fusionner',
                import_replace: 'Remplacer',
            },
        };
    }

    setLanguage(lang) {
        if (this.translations[lang]) {
            this.currentLanguage = lang;
            localStorage.setItem('language', lang);
            document.documentElement.lang = lang;
        }
    }

    getCurrentLanguage() {
        return this.currentLanguage;
    }
    
    // HELIOS: NOUVELLE MÉTHODE UTILITAIRE
    getOtherLanguage() {
        return this.currentLanguage === 'en' ? 'fr' : 'en';
    }

    translate(key, variables = null) {
        let translation = this.translations[this.currentLanguage]?.[key];

        if (!translation) {
            console.warn(
                `Translation missing for key: ${key} in language: ${this.currentLanguage}`
            );
            return key;
        }

        if (variables) {
            for (const varKey in variables) {
                const regex = new RegExp(`{${varKey}}`, 'g');
                translation = translation.replace(regex, variables[varKey]);
            }
        }
        return translation;
    }
}